// eslint-disable-next-line import/no-extraneous-dependencies
import { MuiTelInput, matchIsValidTel } from "mui-tel-input"
// eslint-disable-next-line import/no-extraneous-dependencies
import "flag-icons/css/flag-icons.min.css"
import React, {
    ReactElement,
    useState,
    useEffect,
    useCallback,
    useRef,
} from "react"

type GetFlagElement = (
    isoCode: string,
    {
        countryName,
        isSelected,
        imgProps,
    }: {
        countryName: string
        isSelected: boolean
        imgProps: React.ComponentPropsWithRef<"img">
    }
) => React.ReactElement

const getFlagElement: GetFlagElement = (isoCode) => (
    <span
        className={`fi fi-${isoCode.toLowerCase()}`}
        style={{
            width: "1.5em",
            height: "1em",
        }}
    />
)

interface ValidatedPhoneNumberProps {
    value: string
    label: string
    helperText: string
    onChange: (value: string) => void
    onValidationChange: (isValid: boolean, error: string) => void
    required: boolean
    disabled: boolean
}

export const ValidatedPhoneNumber = ({
    value,
    label,
    helperText = "",
    onChange,
    onValidationChange,
    required = false,
    disabled = false,
}: ValidatedPhoneNumberProps): ReactElement => {
    const [error, setError] = useState("")
    const [internalValue, setInternalValue] = useState(value)
    const [valueToValidate, setValueToValidate] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const validatePhoneNumber = useCallback(
        (phoneValue: string, isSubmit = false): boolean => {
            if (!phoneValue) {
                const errorMsg = required ? "Phone number is required" : ""
                setError(errorMsg)
                onValidationChange?.(!required, errorMsg)
                return !required
            }

            // More strict validation for complete numbers
            const isValidComplete = matchIsValidTel(phoneValue, {
                continents: ["EU", "NA", "OC", "AS"], // Europe, North America, Oceania, Asia
            })

            // Check for minimum length to ensure it's not just an area code
            // Most international phone numbers are at least 8 digits (excluding country code)
            const digitsOnly = phoneValue.replace(/\D/g, "")
            const hasMinimumLength = digitsOnly.length >= 8
            const isValid = isValidComplete && hasMinimumLength

            if (!isValid) {
                let errorMsg = "Please enter a valid phone number"
                if (isValidComplete && !hasMinimumLength) {
                    errorMsg = "Please enter a complete phone number"
                }
                setError(errorMsg)
                onValidationChange?.(false, errorMsg)
                return false
            }

            setError("")
            onValidationChange?.(true, "")
            return true
        },
        [required, onValidationChange]
    )

    // Sync internal value with prop value when it changes externally
    useEffect(() => {
        if (value !== internalValue) {
            setInternalValue(value)
        }
    }, [value, internalValue])

    // Handle validation with timeout
    useEffect(() => {
        if (valueToValidate !== null) {
            const timeoutId = setTimeout(() => {
                validatePhoneNumber(valueToValidate)
                setValueToValidate(null)
            }, 0)

            return () => clearTimeout(timeoutId)
        }
        return undefined // Explicit return to satisfy consistent-return rule
    }, [valueToValidate, validatePhoneNumber])

    const handlePhoneChange = (phoneValue: string): void => {
        setInternalValue(phoneValue)
        onChange(phoneValue)
        // Queue the validation to happen asynchronously
        setValueToValidate(phoneValue)
    }

    // Validate initial value
    useEffect(() => {
        if (value) {
            validatePhoneNumber(value)
        }
    }, [validatePhoneNumber, value]) // Added value as a dependency since it's used in the effect

    return (
        <MuiTelInput
            fullWidth
            margin="normal"
            label={label}
            value={internalValue}
            defaultCountry="GB"
            preferredCountries={["GB", "IE", "US", "CA", "AU"]}
            getFlagElement={getFlagElement}
            onChange={handlePhoneChange}
            error={!!error}
            helperText={error || helperText}
            variant="outlined"
            size="medium"
            required={required}
            disabled={disabled}
            inputRef={inputRef}
            forceCallingCode
            onBlur={() => validatePhoneNumber(internalValue, true)}
            MenuProps={{
                PaperProps: {
                    sx: {
                        maxHeight: 240,
                        mt: 0.5,
                        borderRadius: 1,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    },
                },
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                disablePortal: false,
            }}
            sx={{
                "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "background.paper",
                    "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "primary.main",
                        },
                    },
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 2,
                        },
                    },
                },
                "& .MuiInputLabel-root": {
                    fontWeight: 500,
                },
            }}
        />
    )
}
